import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Contact } from "../../service/contact";
import { Mail, Globe, Calendar, Building2, User2, Clock } from "lucide-react";
import { cn } from "@/lib/utils";

interface ContactActionsProps {
  contact: Contact;
}

export function ContactActions({ contact }: ContactActionsProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        View
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-border/50 bg-background/95 dark:bg-background/95 mx-auto max-w-2xl rounded-xl p-0 shadow-2xl backdrop-blur-sm">
          <div className="border-border/10 sticky top-0 z-10 border-b bg-inherit p-6">
            <DialogHeader>
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <DialogTitle className="text-2xl font-semibold tracking-tight">
                    {contact.name || "No Name"}
                  </DialogTitle>
                  <div className="flex items-center gap-2">
                    <p className="text-muted-foreground/90 font-medium">
                      {contact.email || "No Email"}
                    </p>
                  </div>
                </div>
                <Badge variant={contact.archived ? "secondary" : "default"}>
                  {contact.archived ? "Archived" : "Active"}
                </Badge>
              </div>
            </DialogHeader>
          </div>

          <div className="max-h-[calc(85vh-8rem)] overflow-y-auto">
            <div className="grid gap-6 p-6">
              {/* Contact Information */}
              <div>
                <h3 className="text-muted-foreground mb-3 text-sm font-medium">
                  Contact Information
                </h3>
                <div className="bg-muted/30 hover:bg-muted/40 relative overflow-hidden rounded-lg border p-4 transition-colors">
                  <div className="to-primary/0 absolute inset-0 bg-gradient-to-br from-blue-500/5 opacity-50"></div>
                  <div className="grid gap-4">
                    <div className="flex items-center gap-3">
                      {contact.picture && (
                        <img
                          src={contact.picture}
                          alt={contact.name || "Profile"}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                      )}
                      <div className="space-y-1">
                        <p className="text-sm font-medium">
                          {contact.name || "No Name"}
                        </p>
                        <p className="text-muted-foreground text-sm">
                          {contact.domain || "No Domain"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Company Details */}
              {contact.account && (
                <div>
                  <h3 className="text-muted-foreground mb-3 text-sm font-medium">
                    Company Details
                  </h3>
                  <div className="bg-muted/30 hover:bg-muted/40 relative overflow-hidden rounded-lg border p-4 transition-colors">
                    <div className="to-primary/0 absolute inset-0 bg-gradient-to-br from-purple-500/5 opacity-50"></div>
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <p className="text-sm font-medium">
                          {contact.account.name}
                        </p>
                      </div>
                      {contact.account.picture && (
                        <div className="bg-background dark:bg-background h-10 w-10 rounded-lg p-1.5 shadow-sm">
                          <img
                            src={contact.account.picture}
                            alt={contact.account.name || ""}
                            className="h-full w-full object-contain"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Timeline */}
              <div>
                <h3 className="text-muted-foreground mb-3 text-sm font-medium">
                  Timeline
                </h3>
                <div className="bg-muted/30 hover:bg-muted/40 relative overflow-hidden rounded-lg border p-4 transition-colors">
                  <div className="to-primary/0 absolute inset-0 bg-gradient-to-br from-green-500/5 opacity-50"></div>
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-muted-foreground mb-1.5 text-sm font-medium">
                        Created
                      </h4>
                      <p className="text-sm font-medium">
                        {new Date(contact.creation_date).toLocaleDateString(
                          undefined,
                          {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          },
                        )}
                      </p>
                    </div>
                    {contact.last_email_date && (
                      <div>
                        <h4 className="text-muted-foreground mb-1.5 text-sm font-medium">
                          Last Contact
                        </h4>
                        <p className="text-sm font-medium">
                          {new Date(contact.last_email_date).toLocaleDateString(
                            undefined,
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            },
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Owner Information */}
              {contact.owner && (
                <div>
                  <h3 className="text-muted-foreground mb-3 text-sm font-medium">
                    Owner Details
                  </h3>
                  <div className="bg-muted/30 hover:bg-muted/40 relative overflow-hidden rounded-lg border p-4 transition-colors">
                    <div className="to-primary/0 absolute inset-0 bg-gradient-to-br from-yellow-500/5 opacity-50"></div>
                    <div className="flex items-center gap-3">
                      {contact.owner.picture && (
                        <img
                          src={contact.owner.picture}
                          alt={contact.owner.name}
                          className="h-8 w-8 rounded-full object-cover"
                        />
                      )}
                      <div className="space-y-1">
                        <p className="text-sm font-medium">
                          {contact.owner.name}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Tags */}
              {contact.tags && contact.tags.length > 0 && (
                <div>
                  <h3 className="text-muted-foreground mb-3 text-sm font-medium">
                    Tags
                  </h3>
                  <div className="bg-muted/30 hover:bg-muted/40 relative overflow-hidden rounded-lg border p-4 transition-colors">
                    <div className="to-primary/0 from-primary/5 absolute inset-0 bg-gradient-to-br opacity-50"></div>
                    <div className="flex flex-wrap gap-2">
                      {(contact.tags as string[]).map((tag, index) => (
                        <Badge key={index} variant="secondary">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

interface InfoItemProps {
  icon: React.ReactNode;
  label: string;
  value?: string | null;
  image?: string | null;
}

function InfoItem({ icon, label, value, image }: InfoItemProps) {
  return (
    <div className="flex items-start gap-2">
      <div className="text-muted-foreground mt-1">{icon}</div>
      <div className="space-y-1">
        <p className="text-muted-foreground text-sm">{label}</p>
        <div className="flex items-center gap-2">
          {image && (
            <img
              src={image}
              alt={value || ""}
              className="h-4 w-4 rounded-full object-cover"
            />
          )}
          <p className="font-medium">{value || "N/A"}</p>
        </div>
      </div>
    </div>
  );
}
